import { useState } from 'react';
import {
  Box,
  Chip,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useQueryClient } from '@tanstack/react-query';

import { useEnvironments } from '@/api/hooks';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { SepEnvironment } from '@/types/api';

import { ENVIRONMENT_STYLE, ENVIRONMENTS, setEnvironment } from './store';
import { useEnvironment } from './EnvironmentProvider';

/**
 * Which SEP installation everything on screen belongs to.
 *
 * Sits in the app bar next to the title and carries the environment's colour
 * itself — the bar around it stays SEP green. It reads as a badge rather than a
 * plain dropdown, which is enough to be noticed without repainting the chrome.
 */
export function EnvironmentSwitcher() {
  const environment = useEnvironment();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { data: available } = useEnvironments();

  const style = ENVIRONMENT_STYLE[environment];

  const infoFor = (key: SepEnvironment) => available?.find((entry) => entry.key === key);

  const choose = (next: SepEnvironment) => {
    setAnchor(null);
    if (next === environment) return;
    setEnvironment(next);
    // Cache keys are environment-scoped, so nothing stale can be served — but
    // the previous environment's entries are now dead weight and would go on
    // refetching in the background.
    queryClient.clear();
  };

  return (
    <>
      <Tooltip title={`${style.label}: ${style.description}`}>
        <Box
          component="button"
          type="button"
          onClick={(event) => setAnchor(event.currentTarget)}
          aria-label={`Umgebung wechseln — aktuell ${style.label}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: style.colour,
            // A white hairline separates the badge from the green bar behind it.
            // Two saturated colours meeting edge to edge — red on green above
            // all — shimmer where they touch; the gap stops that.
            border: '1px solid rgba(255,255,255,0.9)',
            color: '#fff',
            cursor: 'pointer',
            font: 'inherit',
            fontWeight: 700,
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap',
            '&:hover': { filter: 'brightness(1.1)' },
          }}
        >
          {isMobile ? style.short : style.label}
          <ExpandMoreIcon sx={{ fontSize: 18 }} />
        </Box>
      </Tooltip>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {ENVIRONMENTS.map((key) => {
          const info = infoFor(key);
          const entry = ENVIRONMENT_STYLE[key];
          return (
            <MenuItem key={key} selected={key === environment} onClick={() => choose(key)}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: entry.colour,
                  mr: 1.5,
                  flexShrink: 0,
                }}
              />
              <ListItemText
                primary={entry.label}
                secondary={entry.description}
                slotProps={{
                  primary: { sx: { fontWeight: key === environment ? 700 : 400 } },
                  secondary: { sx: { fontSize: '0.75rem' } },
                }}
              />
              {/* An installation with no configured host is still selectable —
                  its licence data lives in our database and is perfectly
                  readable. Only the parts that talk to SEP are unavailable, and
                  saying which is more use than greying the whole thing out. */}
              {info && !info.canSync && (
                <Tooltip title="Kein SEP-Host konfiguriert — Benutzerimport nicht möglich.">
                  <WarningAmberOutlinedIcon
                    sx={{ fontSize: 18, ml: 1, color: 'warning.main' }}
                  />
                </Tooltip>
              )}
              {key === environment && <CheckIcon sx={{ fontSize: 18, ml: 1 }} />}
            </MenuItem>
          );
        })}
        <Box sx={{ px: 2, pt: 1, pb: 0.5, maxWidth: 320 }}>
          <Typography variant="caption" color="text.secondary">
            Ein Wechsel ändert, welche Umgebung Sie verwalten — Ihre Anmeldung
            bleibt dieselbe.
          </Typography>
        </Box>
      </Menu>
    </>
  );
}

/**
 * Shown when a response says it answered for a different environment than the
 * one selected — a stale request that outlived a switch.
 */
export function EnvironmentMismatch({ answered }: { answered: SepEnvironment }) {
  const environment = useEnvironment();
  return (
    <Chip
      color="error"
      variant="outlined"
      icon={<WarningAmberOutlinedIcon />}
      label={`Diese Daten stammen aus ${ENVIRONMENT_STYLE[answered].label}, angezeigt wird ${ENVIRONMENT_STYLE[environment].label}. Bitte neu laden.`}
      sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
    />
  );
}
